/**
 * Centralised react-query key factory.
 *
 * Every key is an array literal built here so invalidation after a mutation can
 * target a whole subtree (`queryKeys.matches.all`) without guessing at strings
 * scattered across features.
 */

/**
 * Any serialisable filter object. Deliberately structural: every feature passes
 * its own narrow query type straight into the key factory.
 */
export type ListParams = object;

export const queryKeys = {
  session: () => ['session'] as const,

  platform: {
    all: ['platform'] as const,
    ui: () => ['platform', 'ui'] as const,
  },

  me: {
    all: ['me'] as const,
    profile: () => ['me', 'profile'] as const,
    dashboard: () => ['me', 'dashboard'] as const,
    predictions: (params?: ListParams) => ['me', 'predictions', params ?? {}] as const,
    ledger: (params?: ListParams) => ['me', 'ledger', params ?? {}] as const,
  },

  rounds: {
    all: ['rounds'] as const,
    list: (params?: ListParams) => ['rounds', 'list', params ?? {}] as const,
    detail: (roundId: string) => ['rounds', 'detail', roundId] as const,
    summary: (roundId: string) => ['rounds', 'summary', roundId] as const,
    overallRating: () => ['rounds', 'overall-rating'] as const,
  },

  matches: {
    all: ['matches'] as const,
    list: (params?: ListParams) => ['matches', 'list', params ?? {}] as const,
    detail: (matchId: string) => ['matches', 'detail', matchId] as const,
    predictionPreview: (matchId: string) => ['matches', 'preview', matchId] as const,
    predictionHistory: (matchId: string) => ['matches', 'history', matchId] as const,
  },

  admin: {
    all: ['admin'] as const,
    teams: (params?: ListParams) => ['admin', 'teams', params ?? {}] as const,
    rounds: (params?: ListParams) => ['admin', 'rounds', params ?? {}] as const,
    matches: (params?: ListParams) => ['admin', 'matches', params ?? {}] as const,
    players: (params?: ListParams) => ['admin', 'players', params ?? {}] as const,
    player: (playerId: string) => ['admin', 'players', 'detail', playerId] as const,
    playerLedger: (playerId: string, params?: ListParams) =>
      ['admin', 'players', 'ledger', playerId, params ?? {}] as const,
    invitations: (params?: ListParams) => ['admin', 'invitations', params ?? {}] as const,
  },

  super: {
    all: ['super'] as const,
    settings: () => ['super', 'settings'] as const,
    auditLog: (params?: ListParams) => ['super', 'audit-log', params ?? {}] as const,
    platformLedger: (params?: ListParams) => ['super', 'platform-ledger', params ?? {}] as const,
    platformBalance: () => ['super', 'platform-balance'] as const,
    administrators: () => ['super', 'administrators'] as const,
  },
} as const;
